import * as React from "react";
import { connect } from "react-redux";
import LoadingFullScreen from "../../../components/LoadingFullScreen";
import { RootState } from "../../types";
import {
  getConversation,
  setRouteState,
  validateDateOfBirth,
  registerParticipant,
  authenticateParticipant,
  setSecureMessageStep,
  forgotPin
} from "../actions";
import {
  SecureMessageStep,
  ParticipantDobVerfication,
  ParticipantRegistration,
  ParticipantAuthentication
} from "../types";
import { RouteComponentProps } from "react-router";
import "./SecureMessage.scss";
import ValidateDob from "../components/ValidateDob";
import SetPassword from "../components/SetPassword";
import ValidatePassword from "../components/ValidatePassword";
import ConversationViewer from "../../messaging/components/ConversationViewer";
import { FormikErrors } from "formik";
import { Conversation } from "../../messaging/types";
import PendoDataProvider from "../../../components/PendoDataProvider";

interface StoreProps {
  loading: boolean;
  conversation: Conversation | null;
  step: SecureMessageStep | null;
  dateOfBirth: string;
  errorMessage: string;
}

interface DispatchProps {
  authenticateParticipant: (
    params: ParticipantAuthentication,
    onError: (error: FormikErrors<ParticipantAuthentication>) => void
  ) => void;
  getConversation: (id: string) => void;
  setRouteState: (messageId: string, pinSet: boolean, participantType: string) => void;
  registerParticipant: (
    params: ParticipantRegistration,
    onError: (error: FormikErrors<ParticipantRegistration>) => void
  ) => void;
  validateDateOfBirth: (
    params: ParticipantDobVerfication,
    onError: (error: FormikErrors<ParticipantDobVerfication>) => void
  ) => void;
  setSecureMessageStep: (step: SecureMessageStep) => void;
  forgotPin: () => void;
}

interface OwnState {
  messageId: string;
  participantType: string;
  pinSet: boolean;
}

type Props = OwnState & DispatchProps & StoreProps & RouteComponentProps<{ messageId: string }>;

class SecureMessageContainer extends React.PureComponent<Props, OwnState> {
  public constructor(props: Props) {
    super(props);

    const params = this.getUrlParams();

    this.state = {
      messageId: this.props.match.params.messageId,
      participantType: params.get("participantType") || "",
      pinSet: params.get("pinSet") === "True"
    };
  }

  public componentDidMount = () => {
    this.props.setRouteState(this.state.messageId, this.state.pinSet, this.state.participantType);
  };

  private getUrlParams(): URLSearchParams {
    if (!this.props.location.search) return new URLSearchParams();
    return new URLSearchParams(this.props.location.search);
  }

  private renderComponent() {
    switch (this.props.step) {
      case SecureMessageStep.ValidateDob:
        return (
          <ValidateDob
            participantType={this.state.participantType}
            validateDateOfBirth={this.props.validateDateOfBirth}
            errorMessage={this.props.errorMessage}
          />
        );
      case SecureMessageStep.SetPassword:
        return (
          <SetPassword
            registerParticipant={this.props.registerParticipant}
            dateOfBirth={this.props.dateOfBirth}
          />
        );
      case SecureMessageStep.ValidatePassword:
        return (
          <ValidatePassword
            authenticateParticipant={this.props.authenticateParticipant}
            setSecureMessageStep={this.props.setSecureMessageStep}
            forgotPin={this.props.forgotPin}
            errorMessage={this.props.errorMessage}
          />
        );
      case SecureMessageStep.ViewMessage:
        return (
          <ConversationViewer
            conversationId={this.props.conversation ? this.props.conversation.id : ""}
            isPatient={true}
          />
        );
      case SecureMessageStep.InvalidSession:
        return (
          <div className="m-15">
            Session is invalid. Please return to your SMS application and click the link again to
            view the message.
          </div>
        );
      default:
        return null;
    }
  }

  public render() {
    const { loading } = this.props;
    const pendoDataProviderProps = {
      isAnonymous: true,
      participantType: this.state.participantType
    };
    return (
      <>
        <PendoDataProvider {...pendoDataProviderProps} />
        {loading && <LoadingFullScreen css="top" />}
        <div className={"secure-message-container mobile-container"}>{this.renderComponent()}</div>
      </>
    );
  }
}

export default connect<StoreProps, DispatchProps, {}, RootState>(
  state => ({
    loading: state.securemessage.loading,
    conversation: state.securemessage.data.conversation,
    step: state.securemessage.data.step,
    dateOfBirth: state.securemessage.data.dateOfBirth,
    errorMessage: state.securemessage.errorMessage
  }),
  {
    authenticateParticipant,
    getConversation,
    registerParticipant,
    setRouteState,
    validateDateOfBirth,
    setSecureMessageStep,
    forgotPin
  }
)(SecureMessageContainer);
